import {
  CaretLeftIcon,
  CaretRightIcon,
  CrownSimpleIcon,
  FunnelIcon,
  TicketIcon,
} from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";

import { useAccountCurrency } from "@/client/services/currency/currency";
import { useDialog } from "@/client/stores/dialog";
import {
  AMBASSADOR_PAGE_SIZE,
  type AmbassadorStatusFilter,
  type AmbassadorUsage,
} from "../types/ambassador";

type AmbassadorUsagesListProps = {
  usages: AmbassadorUsage[];
  activeCount: number;
  inactiveCount: number;
  filter: AmbassadorStatusFilter;
  page: number;
  total: number;
  pageSize: number;
  onFilterChange: (filter: AmbassadorStatusFilter) => void;
  onPageChange: (page: number) => void;
};

const formatDate = (value: string) => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Date inconnue";
  }

  return new Intl.DateTimeFormat("fr-FR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
};

const StatusBadge = ({ status }: { status: AmbassadorUsage["status"] }) => {
  const active = status === "active";

  return (
    <span
      className={cn(
        "inline-flex rounded-full px-2.5 py-1",
        "text-[10px] font-semibold",
        active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600",
      )}
    >
      {active ? "Actif" : "Inactif"}
    </span>
  );
};

export const AmbassadorUsagesList = ({
  usages,
  activeCount,
  inactiveCount,
  filter,
  page,
  total,
  pageSize,
  onFilterChange,
  onPageChange,
}: AmbassadorUsagesListProps) => {
  const { format } = useAccountCurrency();

  const formatAmount = (amount: number | null) => (amount === null ? "—" : format(amount));

  const { open: openOffers } = useDialog("forfait");

  const handleOpenOffers = () => {
    openOffers("create", {
      id: "forfait",
    });
  };

  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  const safePage = Math.min(page, pageCount);

  const visibleUsages = usages;

  const firstVisible = total === 0 ? 0 : (safePage - 1) * pageSize + 1;

  const lastVisible = Math.min(safePage * pageSize, total);

  return (
    <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <header className="flex flex-col gap-3 border-b border-slate-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-sm font-bold text-indigo-950 sm:text-base">
              Utilisations de mon code
            </h2>

            <span className="text-[11px] text-slate-500">
              {activeCount} actif
              {activeCount > 1 ? "s" : ""}
              {" · "}
              {inactiveCount} inactif
              {inactiveCount > 1 ? "s" : ""}
            </span>
          </div>

          <p className="mt-1 text-[11px] text-slate-600 sm:text-xs">
            Suivez les achats effectués avec votre code promo.
          </p>
        </div>

        <label className="relative block w-full sm:w-auto">
          <span className="sr-only">Filtrer les utilisations</span>

          <FunnelIcon
            aria-hidden
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-indigo-700"
          />

          <select
            value={filter}
            onChange={(event) => {
              onFilterChange(event.target.value as AmbassadorStatusFilter);
            }}
            className={cn(
              "h-10 w-full appearance-none rounded-xl",
              "border border-slate-200 bg-white",
              "pl-9 pr-9 text-xs font-semibold text-indigo-950",
              "outline-none transition-colors",
              "focus:border-indigo-500 focus:ring-2",
              "focus:ring-indigo-100 sm:min-w-44",
            )}
          >
            <option value="all">Tous les statuts</option>

            <option value="active">Actifs</option>

            <option value="inactive">Inactifs</option>
          </select>
        </label>
      </header>

      {visibleUsages.length === 0 ? (
        <div className="flex min-h-[235px] flex-col items-center justify-center px-5 py-8 text-center sm:min-h-[260px]">
          <span className="grid size-16 place-items-center rounded-full bg-gradient-to-br from-indigo-50 to-violet-100 text-indigo-600">
            <TicketIcon aria-hidden size={32} weight="duotone" />
          </span>

          <h3 className="mt-4 text-base font-bold text-indigo-950">Aucune utilisation</h3>

          <p className="mt-1.5 max-w-md text-xs leading-5 text-slate-600">
            Les achats réalisés avec votre code promo apparaîtront ici.
          </p>

          <button
            type="button"
            onClick={handleOpenOffers}
            className={cn(
              "mt-4 flex min-h-10 items-center gap-2",
              "rounded-xl bg-indigo-700 px-4",
              "text-xs font-semibold text-white",
              "transition-colors hover:bg-indigo-800",
              "focus-visible:outline-none",
              "focus-visible:ring-2",
              "focus-visible:ring-indigo-500",
            )}
          >
            <CrownSimpleIcon aria-hidden size={17} weight="fill" className="text-amber-300" />
            Découvrir les offres
          </button>
        </div>
      ) : (
        <>
          <div className="divide-y divide-slate-100 md:hidden">
            {visibleUsages.map((usage) => (
              <div key={usage.id} className="p-4">
                <div className="flex items-start gap-3">
                  <span className="grid size-9 shrink-0 place-items-center rounded-full bg-indigo-50 text-[11px] font-bold text-indigo-700">
                    {usage.customerInitials}
                  </span>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <p className="truncate text-xs font-bold text-indigo-950">
                        {usage.customerName}
                      </p>

                      <StatusBadge status={usage.status} />
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-[10px]">
                      <div>
                        <p className="text-slate-400">Date d’achat</p>

                        <p className="mt-0.5 font-medium text-slate-700">
                          {formatDate(usage.purchaseDate)}
                        </p>
                      </div>

                      <div>
                        <p className="text-slate-400">Offre</p>

                        <p className="mt-0.5 font-medium text-slate-700">
                          {usage.offerName ?? "Aucune"}
                        </p>
                      </div>

                      <div>
                        <p className="text-slate-400">Prix payé</p>

                        <p className="mt-0.5 font-medium text-slate-700">
                          {formatAmount(usage.amountPaid)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="hidden overflow-x-auto md:block">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-slate-200 text-left">
                  <th className="px-5 py-3 text-[11px] font-semibold text-slate-500">Filleul</th>

                  <th className="px-5 py-3 text-[11px] font-semibold text-slate-500">
                    Date d’achat
                  </th>

                  <th className="px-5 py-3 text-[11px] font-semibold text-slate-500">
                    Offre achetée
                  </th>

                  <th className="px-5 py-3 text-[11px] font-semibold text-slate-500">Prix payé</th>

                  <th className="px-5 py-3 text-[11px] font-semibold text-slate-500">Statut</th>
                </tr>
              </thead>

              <tbody>
                {visibleUsages.map((usage) => (
                  <tr key={usage.id} className="border-b border-slate-100 last:border-b-0">
                    <td className="px-5 py-2.5">
                      <div className="flex items-center gap-2.5">
                        <span className="grid size-8 shrink-0 place-items-center rounded-full bg-indigo-50 text-[10px] font-bold text-indigo-700">
                          {usage.customerInitials}
                        </span>

                        <p className="truncate text-xs font-semibold text-indigo-950">
                          {usage.customerName}
                        </p>
                      </div>
                    </td>

                    <td className="px-5 py-2.5 text-xs text-slate-600">
                      {formatDate(usage.purchaseDate)}
                    </td>

                    <td className="px-5 py-2.5 text-xs text-slate-600">
                      {usage.offerName ?? "Aucune"}
                    </td>

                    <td className="px-5 py-2.5 text-xs text-slate-600">
                      {formatAmount(usage.amountPaid)}
                    </td>

                    <td className="px-5 py-2.5">
                      <StatusBadge status={usage.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <footer className="flex min-h-12 items-center justify-between gap-3 border-t border-slate-200 px-4 sm:px-5">
        <p className="text-[10px] leading-4 text-slate-500 sm:text-[11px]">
          La récompense est versée après validation de l’achat effectué avec votre code.
        </p>

        {usages.length > 0 && (
          <div className="flex shrink-0 items-center gap-2">
            <span className="hidden text-[11px] text-slate-600 sm:inline">
              {firstVisible}–{lastVisible} sur {total}
            </span>

            <button
              type="button"
              disabled={safePage <= 1}
              onClick={() => {
                onPageChange(safePage - 1);
              }}
              aria-label="Page précédente"
              className="grid size-8 place-items-center rounded-lg border border-slate-200 text-indigo-700 disabled:cursor-not-allowed disabled:opacity-35"
            >
              <CaretLeftIcon aria-hidden size={15} weight="bold" />
            </button>

            <button
              type="button"
              disabled={safePage >= pageCount}
              onClick={() => {
                onPageChange(safePage + 1);
              }}
              aria-label="Page suivante"
              className="grid size-8 place-items-center rounded-lg border border-slate-200 text-indigo-700 disabled:cursor-not-allowed disabled:opacity-35"
            >
              <CaretRightIcon aria-hidden size={15} weight="bold" />
            </button>
          </div>
        )}
      </footer>
    </article>
  );
};
